import { type RequestContext } from '~/vendor/cx-web/domain-types';

// Type for the context that will be passed as first parameter to functions
export interface BettingInsightsResultsContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export type BettingInsightsResultsCategoriesConfig = {
  attribute: string;
  labels?: Record<string, { categoryGroupName: string; displayName: string }>;
  order: {
    end?: string[];
    start: string[];
  };
};

export interface BettingInsightsResultsConfig {
  categories: (context: BettingInsightsResultsContext) => BettingInsightsResultsCategoriesConfig | null;
  labelledBy: string;
  propsName: string;
}

// Type alias for the return type of createBettingInsightsResults
export type BettingInsightsResults = Readonly<{
  categories: () => BettingInsightsResultsCategoriesConfig | null;
  labelledBy: string;
  propsName: string;
}>;

/**
 * Factory function to create BettingInsightsResults configuration (functional approach)
 * @param requestContext - The request context
 * @param config - The results configuration
 * @returns Immutable results configuration object
 */
export function createBettingInsightsResults(
  requestContext: RequestContext,
  config: BettingInsightsResultsConfig,
): BettingInsightsResults {
  // Create context object
  const context: BettingInsightsResultsContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
  };

  // Create the result object with wrapper function that injects context
  const result = {
    // Config properties
    labelledBy: config.labelledBy,
    propsName: config.propsName,

    // Wrapper function that injects context as first parameter
    categories: () => config.categories(context),
  };

  return Object.freeze(result);
}
